<script lang="ts" setup>
import type { InputProps, InputTypeState } from '~kui/types/input';

import { useTextareaAutosize } from '@vueuse/core';
import { getInputStateClassesV2, useCharacterCount } from '~kui/composables/useInputState';

interface Props extends InputProps {
  placeholder?: string | undefined;
  errorMessages?: string[];
  disabled?: boolean;
  readOnly?: boolean;
  dataTestId?: string;
  autoGrow?: boolean;
  maxLength?: number;
  rows?: number;
  showCharacterCount?: boolean;
  inputTypeState?: InputTypeState;
}

const props = withDefaults(defineProps<Props>(), {
  errorMessages: () => [],
  placeholder: undefined,
  hideErrorMessage: false,
  dataTestId: undefined,
  autoGrow: true,
  maxLength: 1000,
  rows: 3,
  showCharacterCount: true,
  inputTypeState: undefined,
});

const emit = defineEmits<{
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
}>();

const value = defineModel<string>({ required: false });

const hasError = computed<boolean>(() => props.errorMessages.length > 0);

const { wrapperClasses, elementClasses, fontClasses } = getInputStateClassesV2({
  disabled: computed(() => props.disabled),
  state: computed(() => (hasError.value ? 'error' : props.inputTypeState)),
  wrapperClasses: ['h-auto', 'items-stretch'],
});

const { characterCount, characterCountClass, enforceCharacterLimit } = useCharacterCount(
  computed(() => value.value || ''),
  props.maxLength,
);

const resizeClasses = computed<string>(() => {
  return cva('', {
    variants: {
      autoGrow: {
        true: 'resize-none overflow-hidden',
        false: 'resize-vertical',
      },
    },
  })({ autoGrow: props.autoGrow });
});

const textareaRef = ref<HTMLTextAreaElement | null>(null);

const { textarea: autoGrowTextarea, input: autoGrowInput } = useTextareaAutosize({
  styleProp: 'minHeight',
});

watch(
  () => value.value,
  (newValue) => {
    if (props.autoGrow && autoGrowInput.value !== undefined) {
      autoGrowInput.value = newValue || '';
    }
    if (newValue && newValue.length > props.maxLength) {
      value.value = enforceCharacterLimit(newValue);
    }
  },
);

const focus = () => {
  if (props.autoGrow) {
    autoGrowTextarea.value?.focus();
  }
  else {
    textareaRef.value?.focus();
  }
};

defineExpose({
  focus,
});

const elementId = useId();
</script>

<template>
  <div class="group/kui-input w-full">
    <KUICoreInputLabel
      v-if="!hideLabel"
      :label="label"
      :for="elementId"
      :required="required"
      class="mb-2"
    />
    <div class="relative" :class="wrapperClasses">
      <textarea
        v-if="autoGrow"
        :id="elementId"
        ref="autoGrowTextarea"
        v-model="value"
        :aria-required="required"
        :aria-invalid="hasError"
        :aria-describedby="hasError ? `error-${elementId}` : undefined"
        :class="[fontClasses, elementClasses, resizeClasses]"
        :data-testid="dataTestId"
        :disabled="disabled"
        :placeholder="placeholder"
        :readonly="readOnly"
        :rows="rows"
        @blur="emit('blur', $event)"
        @focus="emit('focus', $event)"
      />
      <textarea
        v-else
        :id="elementId"
        ref="textareaRef"
        v-model="value"
        :aria-required="required"
        :aria-invalid="hasError"
        :aria-describedby="hasError ? `error-${elementId}` : undefined"
        :class="[fontClasses, elementClasses, resizeClasses]"
        :data-testid="dataTestId"
        :disabled="disabled"
        :placeholder="placeholder"
        :readonly="readOnly"
        :rows="rows"
        @blur="emit('blur', $event)"
        @focus="emit('focus', $event)"
      />
    </div>
    <div class="mt-2 flex items-start justify-between">
      <div v-if="!hideErrorMessage && errorMessages.length" class="flex-1">
        <KUICoreInputErrors
          :id="`error-${elementId}`"
          :data-testid="`error-message-${elementId}`"
          :error-messages="errorMessages"
          :font-size="errorMessageSize"
        />
      </div>
      <div
        v-if="showCharacterCount && !disabled"
        class="ml-auto text-sm"
        :class="[characterCountClass]"
      >
        {{ characterCount }} / {{ maxLength }}
      </div>
    </div>
  </div>
</template>
